/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getOptionDecoder,
  getOptionEncoder,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type Codec,
  type Decoder,
  type EncodedAccount,
  type Encoder,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type Option,
  type OptionOrNullable,
  type ReadonlyUint8Array,
} from "@solana/kit";

export const CREDENTIAL_TEMPLATE_DISCRIMINATOR = new Uint8Array([
  229, 129, 99, 91, 179, 207, 66, 75,
]);

export function getCredentialTemplateDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    CREDENTIAL_TEMPLATE_DISCRIMINATOR,
  );
}

export type CredentialTemplate = {
  discriminator: ReadonlyUint8Array;
  /** Reference to the credential type */
  credentialType: Address;
  /** Name of the template */
  name: string;
  /** Image/icon URI for visual display */
  imageUri: string;
  /** Issuer authority (who can issue from this template) */
  issuer: Address;
  /** Whether template is active */
  isActive: boolean;
  /** Total issued from this template */
  totalIssued: bigint;
  /** Creation timestamp */
  createdAt: bigint;
  /** Corresponding Crossmint template ID (for EVM sync) */
  crossmintTemplateId: Option<string>;
  /** PDA bump */
  bump: number;
};

export type CredentialTemplateArgs = {
  /** Reference to the credential type */
  credentialType: Address;
  /** Name of the template */
  name: string;
  /** Image/icon URI for visual display */
  imageUri: string;
  /** Issuer authority (who can issue from this template) */
  issuer: Address;
  /** Whether template is active */
  isActive: boolean;
  /** Total issued from this template */
  totalIssued: number | bigint;
  /** Creation timestamp */
  createdAt: number | bigint;
  /** Corresponding Crossmint template ID (for EVM sync) */
  crossmintTemplateId: OptionOrNullable<string>;
  /** PDA bump */
  bump: number;
};

/** Gets the encoder for {@link CredentialTemplateArgs} account data. */
export function getCredentialTemplateEncoder(): Encoder<CredentialTemplateArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["credentialType", getAddressEncoder()],
      ["name", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["imageUri", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["issuer", getAddressEncoder()],
      ["isActive", getBooleanEncoder()],
      ["totalIssued", getU64Encoder()],
      ["createdAt", getI64Encoder()],
      [
        "crossmintTemplateId",
        getOptionEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
      ["bump", getU8Encoder()],
    ]),
    (value) => ({ ...value, discriminator: CREDENTIAL_TEMPLATE_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link CredentialTemplate} account data. */
export function getCredentialTemplateDecoder(): Decoder<CredentialTemplate> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["credentialType", getAddressDecoder()],
    ["name", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["imageUri", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["issuer", getAddressDecoder()],
    ["isActive", getBooleanDecoder()],
    ["totalIssued", getU64Decoder()],
    ["createdAt", getI64Decoder()],
    [
      "crossmintTemplateId",
      getOptionDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
    ["bump", getU8Decoder()],
  ]);
}

/** Gets the codec for {@link CredentialTemplate} account data. */
export function getCredentialTemplateCodec(): Codec<
  CredentialTemplateArgs,
  CredentialTemplate
> {
  return combineCodec(
    getCredentialTemplateEncoder(),
    getCredentialTemplateDecoder(),
  );
}

export function decodeCredentialTemplate<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<CredentialTemplate, TAddress>;
export function decodeCredentialTemplate<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<CredentialTemplate, TAddress>;
export function decodeCredentialTemplate<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
):
  | Account<CredentialTemplate, TAddress>
  | MaybeAccount<CredentialTemplate, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getCredentialTemplateDecoder(),
  );
}

export async function fetchCredentialTemplate<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<CredentialTemplate, TAddress>> {
  const maybeAccount = await fetchMaybeCredentialTemplate(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeCredentialTemplate<
  TAddress extends string = string,
>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<CredentialTemplate, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeCredentialTemplate(maybeAccount);
}

export async function fetchAllCredentialTemplate(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<CredentialTemplate>[]> {
  const maybeAccounts = await fetchAllMaybeCredentialTemplate(
    rpc,
    addresses,
    config,
  );
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeCredentialTemplate(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<CredentialTemplate>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) =>
    decodeCredentialTemplate(maybeAccount),
  );
}
